import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import ErrorBoundary from '../../components/ErrorBoundary';
import {
  LayoutDashboard,
  CheckSquare,
  FileText,
  Calendar,
  CreditCard,
  LogOut,
  User,
  Home,
  Globe,
  GraduationCap,
  Users,
  UsersRound,
  Settings,
  Mail,
  Lock,
  Sparkles,
  UserPlus,
  Tag,
  Activity,
  Handshake,
  Compass,
  Gift,
  Menu,
  X,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import NotificationBell from '../../components/NotificationBell';
import PartnerMessageBell from '../../components/PartnerMessageBell';
import TrialCountdownStrip from '../../components/TrialCountdownStrip';
import UpgradeWall from '../../components/UpgradeWall';
import SEO from '../../components/SEO';
import { ROLES, FEATURES, canAccess } from '../../constants/tiers';
import { BRAND_LOGO } from '../../constants/brand';

// Map portal tabs → per-page SEO metadata. Used by PortalLayout to auto-inject
// a unique <title>+description per portal route (Step 6 requirement).
const PORTAL_SEO = {
  overview:       { title: 'Dashboard',      desc: 'Your Mundalo member dashboard - Blueprint, Checklist, and the community at a glance.', path: '/portal/dashboard' },
  blueprint:      { title: 'Blueprint',      desc: 'Your personalized Mundalo Relocation Blueprint.',                                        path: '/portal/blueprint' },
  checklist:      { title: 'Checklist',      desc: 'Track every step of your move with the Mundalo Checklist.',                              path: '/portal/checklist' },
  classroom:      { title: 'Classroom',      desc: 'Learn how to move abroad through the Mundalo Classroom curriculum.',                     path: '/portal/classroom' },
  newcomers:      { title: 'Newcomers',      desc: 'Mundalo Newcomers Board - say hello and meet other movers.',                             path: '/portal/community?board=newcomers' },
  communityBoard: { title: 'Community',      desc: 'Connect with Mundalo members in your city and beyond.',                                  path: '/portal/community' },
  events:         { title: 'Events',         desc: 'Mundalo member events - meetups, Q&A sessions, and city tours.',                          path: '/portal/events' },
  housing:        { title: 'Housing',        desc: 'Listings + roommate finder for Mundalo members in Mexico.',                                path: '/portal/housing' },
  partners:       { title: 'Partners',       desc: 'Mundalo affiliate partners - SafetyWing, Airalo, Wise. FTC-disclosed.',                    path: '/portal/partners' },
  localDeals:     { title: 'Local Deals',    desc: 'Mexico-local expat deals - coworking, restaurants, language schools. Updated weekly.',     path: '/portal/local-deals' },
  activities:     { title: 'Activities',     desc: 'Curated Mundalo activities to help you live freely in your new city.',                    path: '/portal/events?type=activities' },
  partnerDeals:   { title: 'Partner Deals',  desc: 'Mundalo partner deals - vetted services for your move.',                                   path: '/portal/community?board=partners' },
  cityGuides:     { title: 'City Guides',    desc: 'Mundalo deep city guides - Budget, Safety, Healthcare, Housing & more across 13 Mexican cities.', path: '/portal/city-guides' },
  peerCircles:    { title: 'Peer Circles',   desc: 'Connect with Mundalo members - directory, DMs, groups, and friend invites.', path: '/portal/peer-circles' },
  adventures:     { title: 'Adventures',     desc: 'Mexico is your playground - paragliding, cenotes, balloons, yacht charters and more.', path: '/portal/adventures' },
};

/* --- Icon map for nav items (lives outside the component for perf) --- */
const ICONS = {
  overview: LayoutDashboard,
  blueprint: Sparkles,
  checklist: CheckSquare,
  newcomers: UserPlus,
  classroom: GraduationCap,
  communityBoard: Users,
  events: Calendar,
  housing: Home,
  partners: Handshake,
  localDeals: Tag,
  activities: Activity,
  partnerDeals: Handshake,
  cityGuides: Globe,
  peerCircles: UsersRound,
  adventures: Compass,
};

/* --- Sidebar grouping: ordered list per section --- */
/* Iter 50: Activities removed from sidebar entirely (still routable). */
/* Iter 145 (Section 1): Peer Circles removed from sidebar entirely. */
const SECTION_ORDER = {
  MOVE: ['overview', 'blueprint', 'checklist', 'newcomers', 'classroom'],
  LIFE: [
    'communityBoard', 'events', 'housing',
    'partners', 'localDeals',
    'adventures',
    'cityGuides',
  ],
};

/* --- Mobile bottom nav (4 items: Home, Blueprint, Community, More) --- */
/* iter 90: pared from 5 → 4 so the 4th tab is a "More" drawer trigger
   that surfaces every secondary nav item without truncation. */
const bottomNavSpec = [
  { id: 'overview', labelEn: 'Home', labelEs: 'Inicio', icon: LayoutDashboard, path: '/portal/dashboard' },
  { id: 'blueprint', labelEn: 'Blueprint', labelEs: 'Blueprint', icon: Sparkles, path: '/portal/blueprint' },
  { id: 'communityBoard', labelEn: 'Community', labelEs: 'Comunidad', icon: Users, path: '/portal/community' },
  { id: 'more', labelEn: 'More', labelEs: 'Más', icon: Menu, path: null },
];

/* --- Mobile "More" drawer items - every nav item that isn't on the bottom bar.
   Order mirrors the desktop sidebar grouping for predictability. --- */
const drawerNavSpec = [
  { id: 'checklist', labelEn: 'Checklist', labelEs: 'Lista', icon: CheckSquare, path: '/portal/checklist', featureId: 'checklist' },
  { id: 'newcomers', labelEn: 'Newcomers Board', labelEs: 'Recién llegados', icon: UserPlus, path: '/portal/community?board=newcomers', featureId: 'newcomers' },
  { id: 'classroom', labelEn: 'Classroom', labelEs: 'Clases', icon: GraduationCap, path: '/portal/classroom', featureId: 'classroom' },
  { id: 'events', labelEn: 'Events', labelEs: 'Eventos', icon: Calendar, path: '/portal/events', featureId: 'events' },
  { id: 'housing', labelEn: 'Housing', labelEs: 'Vivienda', icon: Home, path: '/portal/housing', featureId: 'housing' },
  { id: 'partners', labelEn: 'Partners', labelEs: 'Aliados', icon: Handshake, path: '/portal/partners', featureId: 'partners' },
  { id: 'localDeals', labelEn: 'Local Deals', labelEs: 'Ofertas locales', icon: Tag, path: '/portal/local-deals', featureId: 'localDeals' },
  { id: 'cityGuides', labelEn: 'City Guides', labelEs: 'Guías de ciudad', icon: Globe, path: '/portal/city-guides', featureId: 'cityGuides' },
  { id: 'adventures', labelEn: 'Adventures', labelEs: 'Aventuras', icon: Compass, path: '/portal/adventures', featureId: 'adventures' },
  { id: 'invite', labelEn: 'Invite a Friend', labelEs: 'Invitar', icon: Mail, path: '/portal/peer-circles/invite', featureId: null },
];

const PortalLayout = ({ children, activeTab }) => {
  const { user, logout } = useAuth();
  const { language, setLanguage } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();

  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [upgradeReason, setUpgradeReason] = useState(null);
  const [upgradeFeatureId, setUpgradeFeatureId] = useState(null);
  const [unreadMessages, setUnreadMessages] = useState(0);
  // Peer Circles Phase 2 (iter 75) - DMs + pending contact requests
  const [pcUnread, setPcUnread] = useState(0);
  // iter 90: mobile "More" drawer (left-slide) - opened by the 4th bottom-nav tab.
  const [mobileMoreOpen, setMobileMoreOpen] = useState(false);

  // Auto-close the "More" drawer whenever the user actually navigates so it
  // doesn't stay open across pages.
  useEffect(() => {
    setMobileMoreOpen(false);
  }, [location.pathname, location.search]);

  // Expired trial → redirect to upgrade page
  useEffect(() => {
    if (user?.role === ROLES.EXPIRED_TRIAL && location.pathname !== '/portal/upgrade') {
      navigate('/portal/upgrade', { replace: true });
    }
  }, [user?.role, location.pathname, navigate]);

  // Poll unread admin-message count every 30s
  useEffect(() => {
    if (!user?.id) return;
    let cancelled = false;
    const load = async () => {
      try {
        const token = localStorage.getItem('auth_token');
        const r = await fetch(`${(process.env.REACT_APP_BACKEND_URL || '')}/api/messages/unread-count`, {
          credentials: 'include',
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!cancelled && r.ok) {
          const d = await r.json();
          setUnreadMessages(d.unread || 0);
        }
      } catch { /* noop */ }
    };
    load();
    const iv = setInterval(load, 30_000);
    return () => { cancelled = true; clearInterval(iv); };
  }, [user?.id, location.pathname]);

  // Peer Circles inbox badge: poll every 30s for unread DMs + pending requests.
  // Only relevant for tiers that actually have access; gated 403 is just ignored.
  useEffect(() => {
    if (!user?.id) return;
    if (!canAccess(user?.role, 'peerCircles')) {
      setPcUnread(0);
      return;
    }
    let cancelled = false;
    const load = async () => {
      try {
        const token = localStorage.getItem('auth_token');
        const r = await fetch(`${(process.env.REACT_APP_BACKEND_URL || '')}/api/peer-circles/inbox/unread-count`, {
          credentials: 'include',
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!cancelled && r.ok) {
          const d = await r.json();
          setPcUnread(Number(d.total || 0));
        }
      } catch { /* noop */ }
    };
    load();
    const iv = setInterval(load, 30_000);
    return () => { cancelled = true; clearInterval(iv); };
  }, [user?.id, user?.role, location.pathname]);

  if (user?.role === ROLES.EXPIRED_TRIAL && location.pathname !== '/portal/upgrade') return null;

  // Click handler for nav items - opens UpgradeWall if locked
  const handleNavClick = (featureId, e) => {
    const unlocked = canAccess(user?.role, featureId);
    if (!unlocked) {
      e.preventDefault();
      setUpgradeReason(`Unlock ${FEATURES[featureId].name}`);
      setUpgradeFeatureId(featureId);
      setUpgradeOpen(true);
    }
  };

  /* --- Render a sidebar section (THE MOVE or THE LIFE) --- */
  const renderSection = (sectionKey, sectionLabel, featureIds) => (
    <div className="mb-6">
      <p className="px-4 mb-2 font-mono uppercase tracking-[0.12em] text-[10px] text-[#1A5C6E]/70">
        {sectionLabel}
      </p>
      <ul className="space-y-0.5 px-2">
        {featureIds.map((fid) => {
          const f = FEATURES[fid];
          if (!f) return null;
          const Icon = ICONS[fid] || FileText;
          const unlocked = canAccess(user?.role, fid);
          const isActive = activeTab === fid;
          const isComingSoon = f.comingSoon;

          return (
            <li key={fid}>
              <Link
                to={f.path}
                onClick={(e) => handleNavClick(fid, e)}
                data-testid={`nav-${fid}`}
                className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-all min-h-[40px] text-sm ${
                  isActive
                    ? 'bg-[#2CA8A0]/12 text-[#1A5C6E] font-medium'
                    : unlocked
                      ? 'text-[#0A1F2E]/75 hover:bg-[#F4ECD8] hover:text-[#0A1F2E]'
                      : 'text-[#0A1F2E]/40 hover:bg-[#F4ECD8]/60 cursor-pointer'
                }`}
              >
                <Icon className={`w-4 h-4 shrink-0 ${isActive ? 'text-[#2CA8A0]' : ''}`} />
                <span className="flex-1 truncate">{f.name}</span>

                {/* Unread count for messages (only on communityBoard for now) */}
                {fid === 'communityBoard' && unreadMessages > 0 && (
                  <span className="text-[10px] bg-[#2CA8A0] text-white px-1.5 py-0.5 rounded-full font-medium" data-testid="nav-unread-count">
                    {unreadMessages > 9 ? '9+' : unreadMessages}
                  </span>
                )}

                {/* Peer Circles unread DM + pending request badge */}
                {fid === 'peerCircles' && pcUnread > 0 && (
                  <span
                    className="text-[10px] bg-[#E8A830] text-[#0A1F2E] px-1.5 py-0.5 rounded-full font-bold"
                    data-testid="nav-peer-circles-unread"
                  >
                    {pcUnread > 9 ? '9+' : pcUnread}
                  </span>
                )}

                {/* Coming soon pill */}
                {isComingSoon && unlocked && (
                  <span className="text-[9px] font-mono uppercase tracking-wider text-[#1A5C6E] bg-[#F4ECD8] px-1.5 py-0.5 rounded">
                    Soon
                  </span>
                )}

                {/* Lock icon */}
                {!unlocked && (
                  <Lock className="w-3.5 h-3.5 shrink-0 text-[#0A1F2E]/35" />
                )}

                {/* Preview badge (Classroom for free_trial) */}
                {!unlocked && f.preview && (
                  <span className="text-[9px] font-mono uppercase tracking-wider text-[#2CA8A0] bg-[#2CA8A0]/10 px-1.5 py-0.5 rounded">
                    Preview
                  </span>
                )}
              </Link>
            </li>
          );
        })}
      </ul>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#F9F6EF]">
      {/* Per-page SEO - defaults from the tab metadata above */}
      <SEO
        title={PORTAL_SEO[activeTab]?.title || 'Member Portal'}
        description={PORTAL_SEO[activeTab]?.desc || 'Mundalo Member Portal.'}
        path={PORTAL_SEO[activeTab]?.path || '/portal'}
        noindex
      />

      {/* Trial countdown - visible only when role === free_trial */}
      <TrialCountdownStrip user={user} />

      {/* ========== MOBILE TOP HEADER (< 1024px) ========== */}
      <header
        className="lg:hidden sticky top-0 z-40 flex items-center justify-between px-4 h-14 border-b border-[#EFE6CF] bg-[#F9F6EF]/95 backdrop-blur"
        data-testid="portal-mobile-header"
      >
        <Link to="/portal/dashboard" className="flex items-center gap-2 min-h-[44px]">
          <img src={BRAND_LOGO.green} alt="Mundalo" className="block h-7 w-[140px] object-contain object-left" />
          <span className="font-mono text-[10px] uppercase tracking-wider text-[#1A5C6E]">Portal</span>
        </Link>
        <div className="flex items-center gap-1" data-testid="mobile-header-bell">
          <PartnerMessageBell variant="header" />
          <NotificationBell variant="header" />
        </div>
      </header>

      {/* ========== DESKTOP SIDEBAR (>= 1024px, 256px) ========== */}
      <aside
        className="hidden lg:flex w-64 bg-[#FFFFFF] border-r border-[#EFE6CF] flex-col fixed top-0 left-0 bottom-0 z-30"
        data-testid="portal-desktop-sidebar"
      >
        {/* iter 144o - Brand green Mundalo wordmark in the member-portal sidebar. */}
        <div className="px-6 py-6 border-b border-[#EFE6CF]">
          <Link to="/" className="flex items-center">
            <img src={BRAND_LOGO.green} alt="Mundalo" className="block h-9 w-[180px] object-contain object-left" />
          </Link>
          <p className="font-mono text-[10px] uppercase tracking-[0.12em] text-[#1A5C6E] mt-1.5">Member Portal</p>
        </div>

        {/* Nav */}
        <nav className="flex-1 py-6 overflow-y-auto">
          {renderSection('MOVE', 'The Move', SECTION_ORDER.MOVE)}
          {renderSection('LIFE', 'The Life', SECTION_ORDER.LIFE)}

          {/* Schedule a Call - only visible to tiers that include calls (iter 50) */}
          {['essentials', 'guided', 'connected', 'admin'].includes((user?.role || '').toLowerCase()) && (
            <div className="mb-6">
              <p className="px-4 mb-2 font-mono uppercase tracking-[0.12em] text-[10px] text-[#1A5C6E]/70">Coaching</p>
              <ul className="px-2">
                <li>
                  <Link
                    to="/portal/schedule-call"
                    className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition ${
                      activeTab === 'schedule-call'
                        ? 'bg-[#0A1F2E] text-white'
                        : 'text-[#0A1F2E]/75 hover:bg-[#F4ECD8] hover:text-[#0A1F2E]'
                    }`}
                    data-testid="nav-schedule-call"
                  >
                    <Calendar className="w-4 h-4 shrink-0" />
                    <span>Schedule a Call</span>
                  </Link>
                </li>
              </ul>
            </div>
          )}

          {/* Admin shortcut (only admin role) */}
          {user?.role === ROLES.ADMIN && (
            <div className="mb-6">
              <p className="px-4 mb-2 font-mono uppercase tracking-[0.12em] text-[10px] text-[#1A5C6E]/70">Admin</p>
              <ul className="px-2 space-y-1">
                <li>
                  <Link
                    to="/admin/dashboard"
                    className="flex items-center gap-3 px-3 py-2 rounded-lg text-sm text-[#0A1F2E]/75 hover:bg-[#F4ECD8] hover:text-[#0A1F2E]"
                    data-testid="nav-admin"
                  >
                    <Settings className="w-4 h-4 shrink-0" />
                    <span>Admin Console</span>
                  </Link>
                </li>
                <li>
                  <Link
                    to="/admin/local-deals"
                    className="flex items-center gap-3 px-3 py-2 rounded-lg text-sm text-[#0A1F2E]/75 hover:bg-[#F4ECD8] hover:text-[#0A1F2E]"
                    data-testid="nav-admin-local-deals"
                  >
                    <Tag className="w-4 h-4 shrink-0" />
                    <span>Local Deals</span>
                  </Link>
                </li>
              </ul>
            </div>
          )}

          {/* Notifications */}
          <div className="px-4 mt-2 flex items-center gap-1" data-testid="sidebar-notification-bell">
            <NotificationBell />
            <PartnerMessageBell variant="sidebar" />
          </div>
        </nav>

        {/* Footer - profile / language / logout */}
        <div className="px-4 py-4 border-t border-[#EFE6CF] bg-[#F9F6EF]">
          {/* User card */}
          {user?.id ? (
            <Link
              to={`/portal/members/${user.id}`}
              className="flex items-center gap-3 mb-3 px-2 py-2 rounded-lg hover:bg-[#F4ECD8] transition"
              data-testid="sidebar-user-card"
            >
              {user?.picture ? (
                <img src={user.picture} alt="" className="w-9 h-9 rounded-full object-cover" />
              ) : (
                <div className="w-9 h-9 bg-[#2CA8A0] text-white rounded-full flex items-center justify-center font-medium text-sm">
                  {(user?.first_name?.[0] || user?.name?.[0] || user?.email?.[0] || 'M').toUpperCase()}
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-[#0A1F2E] truncate">
                  {user?.name || user?.first_name || 'Member'}
                </p>
                <p className="text-[11px] text-[#0A1F2E]/60 truncate">{user?.email}</p>
              </div>
            </Link>
          ) : null}

          {/* Language */}
          <div className="flex items-center gap-2 mb-2 px-2">
            <Globe className="w-4 h-4 text-[#1A5C6E]/70" />
            <select
              value={language}
              onChange={(e) => setLanguage(e.target.value)}
              className="bg-transparent text-sm text-[#0A1F2E] cursor-pointer flex-1 outline-none border-0"
              data-testid="portal-language-selector"
            >
              <option value="en">English</option>
              <option value="es">Español</option>
            </select>
          </div>

          {/* Subscription quick link */}
          <Link
            to="/portal/subscription"
            className="flex items-center gap-2 px-2 py-2 rounded text-sm text-[#0A1F2E]/70 hover:bg-[#F4ECD8]"
            data-testid="sidebar-subscription"
          >
            <CreditCard className="w-4 h-4" />
            <span>Subscription</span>
          </Link>

          {/* Redeem promo code - iter 98 */}
          <Link
            to="/portal/redeem"
            className="flex items-center gap-2 px-2 py-2 rounded text-sm text-[#0A1F2E]/70 hover:bg-[#F4ECD8]"
            data-testid="sidebar-redeem"
          >
            <Gift className="w-4 h-4" />
            <span>{language === 'es' ? 'Canjear código' : 'Redeem code'}</span>
          </Link>

          {/* Logout */}
          <button
            onClick={logout}
            className="flex items-center gap-2 px-2 py-2 rounded text-sm text-[#0A1F2E]/70 hover:bg-[#F4ECD8] w-full text-left"
            data-testid="logout-btn"
          >
            <LogOut className="w-4 h-4" />
            <span>{language === 'es' ? 'Cerrar Sesión' : 'Sign Out'}</span>
          </button>
        </div>
      </aside>

      {/* ========== MAIN ========== */}
      <main className="lg:ml-64 min-h-screen flex flex-col pb-16 lg:pb-0">
        {/* iter 85: ErrorBoundary converts any render-time JS crash on a
            portal page from a white-screen into a Mundalo-themed friendly
            fallback. A `key={location.pathname}` reset boundary so navigating
            away from a crashed page recovers cleanly. */}
        <div className="flex-1">
          <ErrorBoundary scope={`portal:${location.pathname}`}>
            {children}
          </ErrorBoundary>
        </div>

        <footer className="border-t border-[#EFE6CF] bg-[#F9F6EF] py-4 px-4 sm:px-6 lg:px-12">
          <p className="text-[#0A1F2E]/55 text-[11px] sm:text-xs text-center leading-relaxed break-words" data-testid="portal-disclaimer">
            Mundalo Blueprint and Checklist content is general information only - not legal, tax, or immigration advice. Use at your own risk.{' '}
            <Link to="/terms" target="_blank" className="text-[#2CA8A0] hover:underline">Terms</Link> ·{' '}
            <Link to="/privacy" target="_blank" className="text-[#2CA8A0] hover:underline">Privacy</Link> ·{' '}
            <a href="mailto:hello@mundalo.com" className="text-[#2CA8A0] hover:underline">hello@mundalo.com</a>
          </p>
        </footer>
      </main>

      {/* ========== MOBILE BOTTOM NAV (< 1024px) ========== */}
      <nav
        className="lg:hidden fixed bottom-0 left-0 right-0 z-40 bg-[#FFFFFF] border-t border-[#EFE6CF] h-16 flex items-stretch justify-around shadow-[0_-1px_8px_rgba(10,31,46,0.04)]"
        data-testid="portal-bottom-nav"
      >
        {bottomNavSpec.map((item) => {
          const Icon = item.icon;
          // "More" tab is active whenever the drawer is open OR the current
          // route maps to a drawer item - so it doesn't go stale.
          const drawerRoute = drawerNavSpec.some((d) => activeTab === d.featureId || activeTab === d.id);
          const isMore = item.id === 'more';
          const active = isMore ? (mobileMoreOpen || drawerRoute) : activeTab === item.id;
          const featureId = isMore ? null : item.id;
          const unlocked = !featureId || canAccess(user?.role, featureId);

          if (isMore) {
            return (
              <button
                key={item.id}
                type="button"
                onClick={() => setMobileMoreOpen((v) => !v)}
                aria-expanded={mobileMoreOpen}
                aria-label={language === 'es' ? item.labelEs : item.labelEn}
                data-testid="bottom-nav-more"
                className={`flex-1 flex flex-col items-center justify-center gap-0.5 min-h-[44px] min-w-[44px] transition ${
                  active ? 'text-[#2CA8A0]' : 'text-[#0A1F2E]/75'
                }`}
              >
                <Icon className="w-5 h-5" />
                <span className="text-[10px] leading-none">
                  {language === 'es' ? item.labelEs : item.labelEn}
                </span>
              </button>
            );
          }

          return (
            <Link
              key={item.id}
              to={item.path}
              onClick={(e) => featureId && handleNavClick(featureId, e)}
              className={`flex-1 flex flex-col items-center justify-center gap-0.5 min-h-[44px] min-w-[44px] transition relative ${
                active ? 'text-[#2CA8A0]' : unlocked ? 'text-[#0A1F2E]/75' : 'text-[#0A1F2E]/35'
              }`}
              data-testid={`bottom-nav-${item.id}`}
              aria-label={language === 'es' ? item.labelEs : item.labelEn}
            >
              <Icon className="w-5 h-5" />
              <span className="text-[10px] leading-none">
                {language === 'es' ? item.labelEs : item.labelEn}
              </span>
              {!unlocked && (
                <Lock className="absolute top-2 right-[20%] w-2.5 h-2.5 text-[#0A1F2E]/40" />
              )}
            </Link>
          );
        })}
      </nav>

      {/* ========== MOBILE "MORE" DRAWER (< 1024px) ========== */}
      {/* Slides in from the LEFT, cream background, scrollable, contains every
          secondary nav item the bottom bar can't show. iter 90. */}
      {mobileMoreOpen && (
        <div
          className="lg:hidden fixed inset-0 z-50"
          data-testid="portal-more-drawer-overlay"
          onClick={() => setMobileMoreOpen(false)}
          role="presentation"
        >
          <div className="absolute inset-0 bg-[#0A1F2E]/45" />
          <aside
            onClick={(e) => e.stopPropagation()}
            className="absolute inset-y-0 left-0 w-[88vw] max-w-[340px] bg-[#F9F6EF] shadow-[6px_0_18px_rgba(10,31,46,0.18)] flex flex-col animate-[slidein_0.22s_ease-out]"
            data-testid="portal-more-drawer"
            role="dialog"
            aria-label="More navigation"
          >
            {/* Drawer header */}
            <div className="flex items-center justify-between px-5 h-14 border-b border-[#EFE6CF] shrink-0">
              <p className="font-display italic text-2xl text-[#0A1F2E] leading-none">More</p>
              <button
                type="button"
                onClick={() => setMobileMoreOpen(false)}
                aria-label="Close drawer"
                className="p-2 -mr-2 text-[#0A1F2E]/70 hover:text-[#0A1F2E]"
                data-testid="portal-more-drawer-close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Drawer body - fully scrollable, every secondary item visible */}
            <div className="flex-1 overflow-y-auto px-3 py-4">
              <p className="px-3 mb-2 font-mono uppercase tracking-[0.12em] text-[10px] text-[#1A5C6E]/70">
                Explore
              </p>
              <ul className="space-y-0.5 mb-5">
                {drawerNavSpec.map((d) => {
                  const Icon = d.icon;
                  const unlocked = !d.featureId || canAccess(user?.role, d.featureId);
                  const isActive = activeTab === d.featureId || activeTab === d.id;
                  return (
                    <li key={d.id}>
                      <Link
                        to={d.path}
                        onClick={(e) => {
                          if (d.featureId) handleNavClick(d.featureId, e);
                          // Click-through closes the drawer (the route-change
                          // effect would also handle it, but this is snappier).
                          setMobileMoreOpen(false);
                        }}
                        data-testid={`drawer-nav-${d.id}`}
                        className={`flex items-center gap-3 px-3 py-3 rounded-lg min-h-[44px] text-[15px] transition-all ${
                          isActive
                            ? 'bg-[#2CA8A0]/12 text-[#1A5C6E] font-medium'
                            : unlocked
                              ? 'text-[#0A1F2E] hover:bg-[#F4ECD8]'
                              : 'text-[#0A1F2E]/45 hover:bg-[#F4ECD8]/60'
                        }`}
                      >
                        <Icon className={`w-5 h-5 shrink-0 ${isActive ? 'text-[#2CA8A0]' : ''}`} />
                        <span className="flex-1 truncate">
                          {language === 'es' ? d.labelEs : d.labelEn}
                        </span>
                        {!unlocked && <Lock className="w-3.5 h-3.5 text-[#0A1F2E]/35 shrink-0" />}
                      </Link>
                    </li>
                  );
                })}
              </ul>

              {/* Schedule a Call - only the tiers that include calls, mirrors
                  the desktop sidebar gating (iter 50). */}
              {['essentials', 'guided', 'connected', 'admin'].includes((user?.role || '').toLowerCase()) && (
                <>
                  <p className="px-3 mb-2 font-mono uppercase tracking-[0.12em] text-[10px] text-[#1A5C6E]/70">
                    Coaching
                  </p>
                  <ul className="space-y-0.5 mb-5">
                    <li>
                      <Link
                        to="/portal/schedule-call"
                        onClick={() => setMobileMoreOpen(false)}
                        className="flex items-center gap-3 px-3 py-3 rounded-lg min-h-[44px] text-[15px] text-[#0A1F2E] hover:bg-[#F4ECD8]"
                        data-testid="drawer-nav-schedule-call"
                      >
                        <Calendar className="w-5 h-5 shrink-0" />
                        <span>Schedule a Call</span>
                      </Link>
                    </li>
                  </ul>
                </>
              )}

              {/* Admin shortcut - admin role only */}
              {user?.role === ROLES.ADMIN && (
                <>
                  <p className="px-3 mb-2 font-mono uppercase tracking-[0.12em] text-[10px] text-[#1A5C6E]/70">
                    Admin
                  </p>
                  <ul className="space-y-0.5 mb-5">
                    <li>
                      <Link
                        to="/admin/dashboard"
                        onClick={() => setMobileMoreOpen(false)}
                        className="flex items-center gap-3 px-3 py-3 rounded-lg min-h-[44px] text-[15px] text-[#0A1F2E] hover:bg-[#F4ECD8]"
                        data-testid="drawer-nav-admin"
                      >
                        <Settings className="w-5 h-5 shrink-0" />
                        <span>Admin Console</span>
                      </Link>
                    </li>
                  </ul>
                </>
              )}

              <p className="px-3 mb-2 font-mono uppercase tracking-[0.12em] text-[10px] text-[#1A5C6E]/70">
                Account
              </p>
              <ul className="space-y-0.5">
                <li>
                  <Link
                    to={user?.id ? `/portal/members/${user.id}` : '/portal/profile'}
                    onClick={() => setMobileMoreOpen(false)}
                    className="flex items-center gap-3 px-3 py-3 rounded-lg min-h-[44px] text-[15px] text-[#0A1F2E] hover:bg-[#F4ECD8]"
                    data-testid="drawer-nav-profile"
                  >
                    <User className="w-5 h-5 shrink-0" />
                    <span>Profile</span>
                  </Link>
                </li>
                <li>
                  <Link
                    to="/portal/subscription"
                    onClick={() => setMobileMoreOpen(false)}
                    className="flex items-center gap-3 px-3 py-3 rounded-lg min-h-[44px] text-[15px] text-[#0A1F2E] hover:bg-[#F4ECD8]"
                    data-testid="drawer-nav-subscription"
                  >
                    <CreditCard className="w-5 h-5 shrink-0" />
                    <span>Subscription</span>
                  </Link>
                </li>
                <li>
                  <Link
                    to="/portal/redeem"
                    onClick={() => setMobileMoreOpen(false)}
                    className="flex items-center gap-3 px-3 py-3 rounded-lg min-h-[44px] text-[15px] text-[#0A1F2E] hover:bg-[#F4ECD8]"
                    data-testid="drawer-nav-redeem"
                  >
                    <Gift className="w-5 h-5 shrink-0" />
                    <span>{language === 'es' ? 'Canjear código' : 'Redeem code'}</span>
                  </Link>
                </li>
              </ul>
            </div>

            {/* Drawer footer - language + logout */}
            <div className="border-t border-[#EFE6CF] bg-[#FCF8EE] px-3 py-3 shrink-0">
              <div className="flex items-center gap-2 px-3 py-2">
                <Globe className="w-4 h-4 text-[#1A5C6E]/70" />
                <select
                  value={language}
                  onChange={(e) => setLanguage(e.target.value)}
                  className="bg-transparent text-sm text-[#0A1F2E] cursor-pointer flex-1 outline-none border-0"
                  data-testid="drawer-language-selector"
                >
                  <option value="en">English</option>
                  <option value="es">Español</option>
                </select>
              </div>
              <button
                type="button"
                onClick={() => { setMobileMoreOpen(false); logout(); }}
                className="w-full flex items-center gap-3 px-3 py-3 rounded-lg min-h-[44px] text-[15px] text-[#0A1F2E] hover:bg-[#F4ECD8] text-left"
                data-testid="drawer-logout-btn"
              >
                <LogOut className="w-5 h-5 shrink-0" />
                <span>{language === 'es' ? 'Cerrar Sesión' : 'Sign Out'}</span>
              </button>
            </div>
          </aside>
        </div>
      )}

      {/* ========== UPGRADE WALL ========== */}
      <UpgradeWall
        open={upgradeOpen}
        onClose={() => setUpgradeOpen(false)}
        reason={upgradeReason}
        featureId={upgradeFeatureId}
      />
    </div>
  );
};

export default PortalLayout;
